import { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldCheck } from "lucide-react";
import type {
  CredentialAvailability,
  CredentialMetadata,
  ModelDefault,
  ModelDefaultRole,
  ProviderInfo,
} from "@racler/protocol";
import { api, errorText } from "../../lib/api.ts";
import { useRelay } from "../../lib/relayContext.tsx";
import { useMachines } from "../../lib/useMachines.ts";
import { AddCredentialModal } from "./AddCredentialModal.tsx";

export interface DirtyHandler {
  isDirty: boolean;
  save: () => Promise<void> | void;
  discard?: () => void;
}

export interface RoleDefinition {
  id: string;
  backendRole: ModelDefaultRole;
  label: string;
  description: string;
}

const ROLES: RoleDefinition[] = [
  {
    id: "chat",
    backendRole: "chat",
    label: "Chat",
    description: "Read-only exploration, analysis, Q&A and planning",
  },
  {
    id: "interactive",
    backendRole: "agent",
    label: "Agent Interactive",
    description: "Interactive development with review and approval in your project",
  },
  {
    id: "autonomous",
    backendRole: "agent",
    label: "Agent Autonomous",
    description: "Independent development work in an isolated Git worktree",
  },
  {
    id: "autocomplete",
    backendRole: "autocomplete",
    label: "Autocomplete",
    description: "Fast inline code completions in Monaco editor",
  },
];

function renderCredentialStatus(
  isOnline: boolean,
  avail?: CredentialAvailability,
  runtimeResult?: { ok: boolean; message: string },
) {
  if (!isOnline) {
    return <span className="status neutral">Machine offline</span>;
  }
  if (!avail || !avail.configured) {
    return <span className="status off">Missing locally</span>;
  }
  if (runtimeResult) {
    if (runtimeResult.ok) {
      return <span className="status on">{runtimeResult.message}</span>;
    }
    const msg = runtimeResult.message.toLowerCase();
    if (
      msg.includes("invalid") ||
      msg.includes("expired") ||
      msg.includes("auth") ||
      msg.includes("401") ||
      msg.includes("403")
    ) {
      return <span className="status off">Invalid or expired key</span>;
    }
    if (msg.includes("rate") || msg.includes("429")) {
      return <span className="status warn">Rate limited</span>;
    }
    return <span className="status neutral">{runtimeResult.message}</span>;
  }
  if (avail.testStatus === "ok") {
    const isStale = avail.lastTestedAt
      ? Date.now() - new Date(avail.lastTestedAt).getTime() > 7 * 86_400_000
      : false;
    if (isStale) {
      return <span className="status neutral">Available · verification stale</span>;
    }
    const dateStr = avail.lastTestedAt
      ? ` (${new Date(avail.lastTestedAt).toLocaleDateString()})`
      : "";
    return <span className="status on">Connected / Verified{dateStr}</span>;
  }
  if (avail.testStatus === "failed") {
    const err = (avail.testError ?? "").toLowerCase();
    if (
      err.includes("invalid") ||
      err.includes("expired") ||
      err.includes("auth") ||
      err.includes("401") ||
      err.includes("403")
    ) {
      return <span className="status off">Invalid or expired key</span>;
    }
    if (err.includes("rate") || err.includes("429")) {
      return <span className="status warn">Rate limited</span>;
    }
    return <span className="status neutral">Could not verify</span>;
  }
  return <span className="status neutral">Available · not verified</span>;
}

export function ProvidersSection({
  onDirtyChange,
}: {
  onDirtyChange?: (handler: DirtyHandler | null) => void;
} = {}) {
  const { machines } = useMachines();
  const [selectedMachineId, setSelectedMachineId] = useState<string>("");
  const [credentials, setCredentials] = useState<CredentialMetadata[]>([]);
  const [savedDefaults, setSavedDefaults] = useState<ModelDefault[]>([]);
  const [pendingDefaults, setPendingDefaults] = useState<ModelDefault[]>([]);
  const [machineModels, setMachineModels] = useState<ProviderInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { ok: boolean; message: string }>>({});
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [defaultsSavedMsg, setDefaultsSavedMsg] = useState<string | null>(null);

  const relay = useRelay();

  useEffect(() => {
    if (machines && machines.length > 0 && !selectedMachineId) {
      const onlineOne = machines.find((m) => m.online) ?? machines[0]!;
      setSelectedMachineId(onlineOne.id);
    }
  }, [machines, selectedMachineId]);

  const selectedMachine = machines?.find((m) => m.id === selectedMachineId);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [credsRes, defsRes] = await Promise.all([
        api.credentials(),
        api.modelDefaults(),
      ]);
      setCredentials(credsRes.credentials);
      setSavedDefaults(defsRes.defaults);
      setPendingDefaults(defsRes.defaults);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  const loadMachineModels = useCallback(async () => {
    if (!selectedMachineId || !selectedMachine?.online) {
      setMachineModels([]);
      return;
    }
    try {
      const res = await relay.machine(selectedMachineId, "agent/ai.models", {});
      setMachineModels(res.providers);
    } catch {
      setMachineModels([]);
    }
  }, [relay, selectedMachineId, selectedMachine?.online]);

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    void loadMachineModels();
  }, [loadMachineModels]);

  const isDirty = useMemo(() => {
    if (savedDefaults.length !== pendingDefaults.length) return true;
    for (const p of pendingDefaults) {
      const s = savedDefaults.find((d) => d.role === p.role);
      if (!s) return true;
      if (s.provider !== p.provider || s.model !== p.model || s.effort !== p.effort) return true;
    }
    return false;
  }, [savedDefaults, pendingDefaults]);

  const saveDefaults = useCallback(async () => {
    setSavingDefaults(true);
    setDefaultsSavedMsg(null);
    try {
      for (const p of pendingDefaults) {
        const s = savedDefaults.find((d) => d.role === p.role);
        if (!s || s.provider !== p.provider || s.model !== p.model || s.effort !== p.effort) {
          await api.saveModelDefault({
            role: p.role,
            provider: p.provider,
            credentialId: p.credentialId,
            model: p.model,
            effort: p.effort,
          });
        }
      }
      const defsRes = await api.modelDefaults();
      setSavedDefaults(defsRes.defaults);
      setPendingDefaults(defsRes.defaults);
      setDefaultsSavedMsg("Model defaults saved successfully");
      setTimeout(() => setDefaultsSavedMsg(null), 3000);
    } catch (e) {
      alert(`Failed to save defaults: ${errorText(e)}`);
      throw e;
    } finally {
      setSavingDefaults(false);
    }
  }, [pendingDefaults, savedDefaults]);

  const discardDefaults = useCallback(() => {
    setPendingDefaults(savedDefaults);
  }, [savedDefaults]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange({
        isDirty,
        save: saveDefaults,
        discard: discardDefaults,
      });
    }
  }, [isDirty, onDirtyChange, saveDefaults, discardDefaults]);

  const handleTest = async (credId: string) => {
    if (!selectedMachineId) return;
    setTestingId(credId);
    try {
      const res = await api.testCredential(selectedMachineId, credId);
      setTestResult((prev) => ({
        ...prev,
        [credId]: {
          ok: res.ok,
          message: res.ok ? `Connected (${res.modelCount ?? 0} models)` : (res.error ?? "Failed"),
        },
      }));
      void loadData();
      void loadMachineModels();
    } catch (e) {
      setTestResult((prev) => ({
        ...prev,
        [credId]: { ok: false, message: errorText(e) },
      }));
    } finally {
      setTestingId(null);
    }
  };

  const handleRemove = async (credId: string) => {
    if (!confirm("Are you sure you want to remove this credential?")) return;
    try {
      await api.deleteCredential(credId, selectedMachineId);
      void loadData();
      void loadMachineModels();
    } catch (e) {
      alert(`Failed to remove: ${errorText(e)}`);
    }
  };

  const handleDefaultChange = (role: ModelDefaultRole, combinedKey: string) => {
    const [provId, modelId] = combinedKey.split("::");
    const p = machineModels.find((x) => x.id === provId);
    const m = p?.models.find((x) => x.id === modelId);
    if (!p || !m) return;

    const credId = credentials.some((c) => c.id === p.id) ? p.id : undefined;
    const effort = m.efforts.length > 0 ? m.efforts[0] : undefined;

    setPendingDefaults((prev) => {
      const next = prev.filter((d) => d.role !== role);
      next.push({
        role,
        provider: p.id,
        credentialId: credId,
        model: m.id,
        effort,
      });
      return next;
    });
  };
  return (
    <>
      <div className="settings-notice">
        <ShieldCheck size={18} className="settings-notice-icon" aria-hidden />
        <div>
          <strong>Encrypted Local Storage:</strong> API keys and provider secrets are encrypted directly in
          your browser for the target machine and stored exclusively in that machine&apos;s local keychain (or
          encrypted keystore). Plaintext keys are never stored on racler.dev servers.
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      {machines && machines.length > 0 && (
        <section className="settings-card set-block" data-testid="machine-selector">
          <div className="settings-card-header">
            <div>
              <h3 className="settings-card-title">Target Machine</h3>
              <p className="settings-card-desc">
                Credentials and model availability are scoped to the selected development machine.
              </p>
            </div>
          </div>
          <select
            value={selectedMachineId}
            onChange={(e) => setSelectedMachineId(e.target.value)}
            style={{ maxWidth: 360 }}
          >
            {machines.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.online ? "Online" : "Offline"})
              </option>
            ))}
          </select>
        </section>
      )}

      {/* Credentials list */}
      <section className="settings-card set-block" data-testid="credentials-list">
        <div className="settings-card-header">
          <div>
            <h3 className="settings-card-title">Credentials</h3>
            <p className="settings-card-desc">
              Configured AI provider keys stored locally on {selectedMachine?.name ?? "this machine"}.
            </p>
          </div>
          <button
            className="action-btn"
            disabled={!selectedMachine?.online}
            onClick={() => setShowAddModal(true)}
          >
            Add Credential
          </button>
        </div>

        {loading && <p className="muted small">Loading credentials…</p>}

        {!loading && credentials.length === 0 && (
          <p className="muted small">No credentials configured yet.</p>
        )}

        {!loading && credentials.length > 0 && (
          <table className="kv">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Label</th>
                <th>Status on {selectedMachine?.name ?? "Machine"}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {credentials.map((c) => {
                const avail = c.machineAvailability.find((m) => m.machineId === selectedMachineId);
                const isOnline = selectedMachine?.online ?? false;
                const result = testResult[c.id];

                return (
                  <tr key={c.id}>
                    <td>
                      <span className="badge">{c.provider.toUpperCase()}</span>
                    </td>
                    <td>
                      <strong>{c.label}</strong>
                      {c.baseUrl && <div className="muted small">{c.baseUrl}</div>}
                    </td>
                    <td>{renderCredentialStatus(isOnline, avail, result)}</td>
                    <td>
                      <button
                        className="link-btn"
                        disabled={!isOnline || testingId === c.id}
                        onClick={() => {
                          void handleTest(c.id);
                        }}
                      >
                        {testingId === c.id ? "Testing…" : "Test"}
                      </button>
                      {" · "}
                      <button
                        className="link-btn danger"
                        onClick={() => {
                          void handleRemove(c.id);
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
      {/* Model Defaults Section */}
      <section className="settings-card set-block" data-testid="model-defaults">
        <div className="settings-card-header">
          <div>
            <h3 className="settings-card-title">Role Model Defaults</h3>
            <p className="settings-card-desc">
              Assign default models for each role. Precedence: temporary chat choice → Project override →
              account default → safe fallback.
            </p>
          </div>
          {isDirty && (
            <button
              className="action-btn"
              disabled={savingDefaults}
              onClick={() => {
                void saveDefaults();
              }}
            >
              {savingDefaults ? "Saving…" : "Save Defaults"}
            </button>
          )}
        </div>

        {defaultsSavedMsg && (
          <p className="small" style={{ color: "#16a34a", margin: 0 }}>
            {defaultsSavedMsg}
          </p>
        )}

        <table className="kv">
          <thead>
            <tr>
              <th>Role</th>
              <th>Description</th>
              <th>Assigned Model</th>
            </tr>
          </thead>
          <tbody>
            {ROLES.map((r) => {
              const assigned = pendingDefaults.find((d) => d.role === r.backendRole);
              const assignedCombined = assigned ? `${assigned.provider}::${assigned.model}` : "";
              const isAssignedAvailable = assigned
                ? machineModels.some(
                    (p) => p.id === assigned.provider && p.models.some((m) => m.id === assigned.model),
                  )
                : true;

              return (
                <tr key={r.id}>
                  <td>
                    <strong>{r.label}</strong>
                  </td>
                  <td className="muted small">{r.description}</td>
                  <td>
                    <select
                      value={assignedCombined}
                      disabled={!selectedMachine?.online || machineModels.every((p) => p.models.length === 0)}
                      onChange={(e) => {
                        handleDefaultChange(r.backendRole, e.target.value);
                      }}
                    >
                      <option value="" disabled>
                        {machineModels.every((p) => p.models.length === 0)
                          ? "No models available"
                          : "Select model…"}
                      </option>
                      {assigned && !isAssignedAvailable && (
                        <option value={assignedCombined} disabled>
                          {assigned.model} (Unavailable on this Machine)
                        </option>
                      )}
                      {machineModels.map((p) =>
                        p.models.map((m) => (
                          <option key={`${p.id}::${m.id}`} value={`${p.id}::${m.id}`}>
                            {m.name} — {p.name}
                          </option>
                        )),
                      )}
                    </select>
                    {assigned && !isAssignedAvailable && (
                      <span className="status off small"> (Unavailable on this Machine)</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {showAddModal && selectedMachine && (
        <AddCredentialModal
          machineId={selectedMachine.id}
          machineName={selectedMachine.name}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            void loadData();
            void loadMachineModels();
          }}
        />
      )}
    </>
  );
}

